import { Component, ElementRef, EventEmitter, Input, OnChanges, OnInit, Output, ViewChild, inject, PLATFORM_ID } from '@angular/core';
import { LoanService } from '@shared/service/loan.service';
import { DocumentStatus } from '@shared/enum';
import { CustomStipulation } from 'src/postapp/models/decision.model';
import { ElementClassName, Info } from '@workflow/ecp-shared/constant/information.const';
import { GlobalService } from '@workflow/ecp-shared/service/global/global.service';
import { EcpSharedModule } from '@workflow/ecp-shared/shared.module';
import { SharedUiLibraryModule } from '@cudirect/shared-ui-library';
import { PdfEmbedComponent } from "@cudirect/shared-ui-library/pdf-embed";
import { isPlatformBrowser } from '@angular/common';

/**
 * Component representing document upload section
 * @Component
 */
@Component({
  selector: 'cu-postapp-esign-document-review',
  templateUrl: './esign-document-review.component.html',
  standalone: true,
  imports: [EcpSharedModule, SharedUiLibraryModule, PdfEmbedComponent]
})

/**
 * Class representing document upload section
 * @export
 * @class DocumentReviewComponent
 * @implements {OnInit}
 */
export class ESignDocumentReviewComponent implements OnInit, OnChanges {
  // Using inject pattern for dependencies
  private readonly loanService = inject(LoanService);
  private readonly globalService = inject(GlobalService);
  private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));

  /** Trigger upload document close event */
  @Output() public modalClose = new EventEmitter<boolean>();
  /** document task details */
  @Input() public stipulation: CustomStipulation;
  /** flag indicates page scrolled to bottom */
  public scrolldowned = false;
  /** flag to show or hide loader */
  public loading = false;
  /** to enable close option */
  public isCloseEnabled = true;
  /** source url for doc */
  public docSource: string;
  public pdfBlob: Blob;
  /** holds element of #closeModel */
  @ViewChild(Info.closeModal) public closeModal: ElementRef;

  /**
   * Initialize while page load
   * memberof DocumentReviewComponent
   */
  public ngOnInit(): void {
    this.setXScrollPosition();
  }

  public ngOnChanges(): void {
    this.geteSignDocument();
  }

  public pdfComplete(loaded): void {
    this.loading = !loaded;
    if (loaded) {
      this.onScroll();
    }
  }

  private setXScrollPosition(): void {
    const leftScroll = 57;
    const Timeout = 2000;
    setTimeout(() => {
      const element = document.getElementsByClassName(ElementClassName.documentReviewContainer);
      if (element && element.length > 0) {
        element[0].scrollLeft = leftScroll;
      }
    }, Timeout);
  }

  private onScroll(): void {
    const textElement = document.getElementById(ElementClassName.portalContent);
    if (!textElement) {
      return;
    }

    const buffer = 200;
    const offset = 2;
    if (textElement.scrollHeight === textElement.clientHeight) {
      this.scrolldowned = true;
      return;
    }
    textElement.addEventListener(ElementClassName.scroll, () => {
      if (textElement.scrollHeight - buffer <= textElement.scrollTop + textElement.offsetHeight - offset) {
        this.scrolldowned = true;
      }
    });
  }

  public geteSignDocument(): void {
    if (
      !this.stipulation?.type ||
      this.stipulation.type !== 'esign' ||
      !this.stipulation.stipulationId ||
      !this.stipulation.status ||
      this.stipulation.status.toLowerCase() !== DocumentStatus.Done
    ) {
      return;
    }
    this.loading = true;
    this.docSource = null;
    this.loanService.getESignDocument(this.stipulation.stipulationId).subscribe(
      (blob: Blob) => {
        this.loading = false;
        this.pdfBlob = blob;
        if (this.isBrowser) {
          this.docSource = window.URL.createObjectURL(blob);
        }
        Promise.resolve(true);
      },
      () => {
        this.loading = false;
        this.close();
      }
    );
  }

  public download(): void {
    if (this.isBrowser) {
      this.globalService.downloadFile(this.pdfBlob, this.stipulation.name + '.pdf', Info.pdfMemeType);
    }
    this.close();
  }

  /**
   * Event trigger to close the upload document section
   */
  public close(): void {
    this.modalClose.emit(true);
    if (this.isBrowser) {
      this.closeModal.nativeElement.click();
    }
  }
}
